import axios from "axios";
import { useContext, useEffect, useState } from "react";
import { AuthContext } from "../../../context/AuthContext";
import { IPerpDataSummary } from "../../../interfaces/perpData";

export interface IFundingData {
  Pair: string;
  Aevo_Funding: number;
  Binance_Funding: number;
  Bybit_Funding: number;
  Net_Funding: number;
  Date?: string;
  count?: number;
}

interface IFundingDataResponse {
  success: boolean;
  message: string;
  data: IFundingData[];
}

interface IFundingDataParams {
  aggregate?: boolean;
  groupByDateOnly?: boolean;
  groupByAssetOnly?: boolean;
  startDate?: string;
  endDate?: string;
  pairs?: string[];
  limit?: number;
}

export const mergeLighterFundingData = (
  fundingData: IFundingData[] | undefined,
  summary: IPerpDataSummary | undefined
): IFundingData[] | undefined => {
  if (!fundingData || !summary?.fundingCoverage?.lighterAvailable) {
    return fundingData;
  }

  const mergedRows = fundingData.map((row) => ({ ...row }));
  const rowByPair = new Map(
    mergedRows.map((row) => [row.Pair.toUpperCase(), row])
  );
  const lighterOnlyRows: IFundingData[] = [];

  summary.pairs.forEach((pair) => {
    if (
      typeof pair.lighterFunding !== "number" ||
      !Number.isFinite(pair.lighterFunding) ||
      typeof pair.lighterMainnetFunding !== "number" ||
      !Number.isFinite(pair.lighterMainnetFunding)
    ) {
      return;
    }

    const lighterFunding = pair.lighterFunding + pair.lighterMainnetFunding;
    const pairKey = pair.instrument.toUpperCase();
    const existingRow = rowByPair.get(pairKey);

    if (existingRow) {
      existingRow.Net_Funding =
        Number(existingRow.Net_Funding || 0) + lighterFunding;
    } else if (lighterFunding !== 0) {
      lighterOnlyRows.push({
        Pair: pair.instrument,
        Aevo_Funding: 0,
        Binance_Funding: 0,
        Bybit_Funding: 0,
        Net_Funding: lighterFunding,
      });
    }
  });

  lighterOnlyRows.sort((a, b) => a.Pair.localeCompare(b.Pair));
  return [...mergedRows, ...lighterOnlyRows];
};

const getFundingDataUrl = () => {
  const baseUrl =
    process.env.REACT_APP_AEVO_API_URL_MAINNET ??
    (typeof window !== "undefined" ? window.location.origin : "");
  const normalizedBase = baseUrl.endsWith("/") ? baseUrl.slice(0, -1) : baseUrl;
  return `${normalizedBase}/api/funding/funding-data`;
};

export const useFundingData = (params: IFundingDataParams) => {
  const { tokenId } = useContext(AuthContext);
  const [data, setData] = useState<IFundingData[] | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setData(undefined);
    setIsLoading(true);

    const fetchFundingData = async () => {
      const queryParams = new URLSearchParams();

      if (params.aggregate) queryParams.append("aggregate", "true");
      if (params.groupByDateOnly) queryParams.append("group_by_date_only", "true");
      if (params.groupByAssetOnly) queryParams.append("group_by_asset_only", "true");
      if (params.startDate) queryParams.append("start_date", params.startDate);
      if (params.endDate) queryParams.append("end_date", params.endDate);
      if (params.pairs?.length) queryParams.append("pair", params.pairs.join(","));
      if (params.limit) queryParams.append("limit", params.limit.toString());

      try {
        const response = await axios.get<IFundingDataResponse>(
          `${getFundingDataUrl()}?${queryParams.toString()}`,
          {
            withCredentials: true,
            headers: {
              Authorization: `Bearer ${tokenId}`,
              "Access-Control-Allow-Origin": "*",
            },
          }
        );

        setData(response.data.data);
      } catch (error) {
        console.error("Error fetching funding data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    if (tokenId) {
      fetchFundingData();
    }
  }, [
    tokenId,
    params.aggregate,
    params.groupByDateOnly,
    params.groupByAssetOnly,
    params.startDate,
    params.endDate,
    params.pairs?.join(","),
    params.limit,
  ]);

  return { data, isLoading };
};
